import {
    OperatorAccess,
    PERMISSIONS,
    PermissionCode,
    can,
} from '../lib/permissions';
import privilegedPaths from './SporttiaPrivilegedPaths';

/**
 * Permiso v8 que exige cada sección del manager antiguo. Una ruta queda
 * protegida por el primer prefijo que la contiene por segmentos completos
 * (`/events` protege `/events`, `/events/:id` y `/events/123/teams`, pero no
 * `/eventsfoo`), así que los prefijos más largos van antes que los que los
 * contienen (`/remittences/generate` antes de `/remittences`).
 *
 * Los códigos son los mismos que usa el sidebar del manager nuevo
 * (`sidebarConfig.ts` y `navigation.ts`), para que los dos gestores protejan
 * igual cada sección. Las rutas que no aparecen aquí (inicio, mi cuenta,
 * ayuda, suscripción…) no exigen ningún permiso.
 */
export const ROUTE_PERMISSIONS: readonly {
    prefix: string;
    permission: PermissionCode;
}[] = [
    // Mi centro
    { prefix: '/devices', permission: PERMISSIONS.DEVICE.VIEW },
    { prefix: '/capacity-monitor', permission: PERMISSIONS.DEVICE.VIEW },
    { prefix: '/busy/schedules', permission: PERMISSIONS.CLOSURE.VIEW },
    { prefix: '/facilities', permission: PERMISSIONS.FACILITY.VIEW },
    { prefix: '/facilitySets', permission: PERMISSIONS.FACILITY.VIEW },
    { prefix: '/rates', permission: PERMISSIONS.RATE.VIEW },
    { prefix: '/frees/schedules', permission: PERMISSIONS.SCHEDULE.VIEW },
    { prefix: '/discounts', permission: PERMISSIONS.DISCOUNT.VIEW },

    // Disponibilidad (cuadrante): enseña las reservas del centro
    { prefix: '/timetable', permission: PERMISSIONS.BOOKING.VIEW },

    // Clientes
    { prefix: '/mships', permission: PERMISSIONS.CUSTOMER.VIEW },
    { prefix: '/groups', permission: PERMISSIONS.GROUP.VIEW },
    { prefix: '/bonos', permission: PERMISSIONS.VOUCHER.VIEW },

    // Reservas (los partidos van con los permisos de reservas)
    { prefix: '/bookings', permission: PERMISSIONS.BOOKING.VIEW },
    {
        prefix: '/games/:id/addPlayer',
        permission: PERMISSIONS.BOOKING.MODIFY,
    },
    { prefix: '/games/:id/sc', permission: PERMISSIONS.BOOKING.VIEW },
    {
        prefix: '/booking/schedules',
        permission: PERMISSIONS.FIXED_BOOKING.VIEW,
    },
    { prefix: '/restrictions', permission: PERMISSIONS.RESTRICTION.VIEW },

    // Eventos y actividades
    { prefix: '/events', permission: PERMISSIONS.EVENT.VIEW },
    { prefix: '/activities', permission: PERMISSIONS.ACTIVITY.VIEW },
    { prefix: '/teachers', permission: PERMISSIONS.ACTIVITY.VIEW },
    { prefix: '/students', permission: PERMISSIONS.ACTIVITY.VIEW },
    { prefix: '/waitingLists', permission: PERMISSIONS.ACTIVITY.VIEW },
    { prefix: '/competitions', permission: PERMISSIONS.COMPETITION.VIEW },

    // Contabilidad
    { prefix: '/payments', permission: PERMISSIONS.ACCOUNTING.VIEW },
    { prefix: '/charges', permission: PERMISSIONS.ACCOUNTING.VIEW },
    { prefix: '/transactions', permission: PERMISSIONS.ACCOUNTING.VIEW },
    {
        prefix: '/remittences/generate',
        permission: PERMISSIONS.REMITTANCE.CREATE,
    },
    { prefix: '/remittences', permission: PERMISSIONS.REMITTANCE.VIEW },
    { prefix: '/sale-point', permission: PERMISSIONS.SALE_POINT.VIEW },
    { prefix: '/tickets', permission: PERMISSIONS.SALE_POINT.VIEW },

    // Informes
    { prefix: '/reports', permission: PERMISSIONS.REPORT.VIEW },
    { prefix: '/stats', permission: PERMISSIONS.REPORT.VIEW },

    // Comunicación
    { prefix: '/messages', permission: PERMISSIONS.COMMUNICATION.VIEW },

    // Configuración y administración
    { prefix: '/configurations', permission: PERMISSIONS.CONFIGURATION.VIEW },
    { prefix: '/roles', permission: PERMISSIONS.ADMINISTRATION.VIEW },
];

const isUnder = (path: string, prefix: string) =>
    path === prefix || path.startsWith(`${prefix}/`);

/** El permiso que exige una ruta, o `undefined` si no exige ninguno. */
export function getRoutePermission(path: string): PermissionCode | undefined {
    return ROUTE_PERMISSIONS.find(({ prefix }) => isUnder(path, prefix))
        ?.permission;
}

/**
 * Protección anterior a los roles: compara el patrón exacto de la ruta con
 * `SporttiaPrivilegedPaths`. Las rutas que no están en ese mapa son libres.
 */
export function checkPrivilegesForPath(
    path: string,
    privileges: readonly number[],
): boolean {
    const privilegedPathsToCheck: Record<string, number> =
        privilegedPaths.paths;
    const privilege = Object.prototype.hasOwnProperty.call(
        privilegedPathsToCheck,
        path,
    )
        ? privilegedPathsToCheck[path]
        : undefined;

    return privilege === undefined || privileges.includes(privilege);
}

export type RouteAccess = {
    rolesEnabled: boolean;
    permissions: readonly string[];
    privileges: readonly number[];
};

/**
 * Si el administrador del centro puede abrir una ruta. Con los roles
 * encendidos manda el permiso v8 de la sección (por prefijo, así que también
 * protege el detalle); con los roles apagados se mantienen los privilegios.
 */
export function canAccessRoute(path: string, access: RouteAccess): boolean {
    if (!access.rolesEnabled) {
        return checkPrivilegesForPath(path, access.privileges);
    }

    const permission = getRoutePermission(path);

    return permission === undefined || can(access, permission);
}

/**
 * Operador sin ningún permiso: sin rol en el centro o con el rol desactivado
 * (`/v8/manager/me` sólo trae `permissions: []`, así que los dos casos se
 * cuentan juntos). Con los roles apagados nunca lo es.
 */
export function hasNoPermissions(access: OperatorAccess): boolean {
    return access.rolesEnabled && access.permissions.length === 0;
}

/** Página a la que se va cuando no hay ninguna otra que abrir. */
export const ACCOUNT_PATH = '/account';

/**
 * Páginas de inicio posibles, por orden de preferencia: primero el cuadrante
 * (la portada de siempre) y después las secciones en el orden del sidebar.
 */
export const LANDING_PATHS: readonly string[] = [
    '/timetable',
    '/bookings',
    '/mships',
    '/groups',
    '/bonos',
    '/events',
    '/activities',
    '/competitions',
    '/payments',
    '/sale-point',
    '/reports',
    '/messages',
    '/facilities',
    '/rates',
    '/configurations',
];

/**
 * La primera página de inicio que el operador puede abrir, o su cuenta si no
 * puede abrir ninguna. Sirve para que "Volver al inicio" no mande a otra 401
 * (con los roles encendidos el cuadrante exige booking.view). Con los roles
 * apagados sigue siendo el cuadrante.
 */
export function getLandingPath(access: RouteAccess): string {
    return (
        LANDING_PATHS.find((path) => canAccessRoute(path, access)) ??
        ACCOUNT_PATH
    );
}
